import React, { memo } from "react"
import { Link } from "gatsby"
import { Spin } from "antd"

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)

// Odoo many2one fields arrive as [id, "Label"] when set, or null/false when
// not — this only reads that shape to decide whether a tag has something to
// show, it does not change what the field contains or how it was fetched.
// `address_id` on every current role resolves to the company's own address,
// labelled "Visiomate" — not a real location — so that value is filtered
// out specifically, same as an empty one, rather than shown as a tag.
const relLabel = field => (Array.isArray(field) && field[1] ? field[1] : null)
const isUsefulTag = tag => Boolean(tag) && tag !== "Visiomate"

// ERP titles can contain Unicode "mathematical bold" letters (a different
// code point per letter than plain Latin, e.g. pasted in from a formatting
// tool) that fall outside this site's font and silently render in a
// fallback typeface. NFKC maps those back to their plain-Latin compatibility
// equivalents so the title renders in the normal page font — display only,
// doesn't touch `car.display_name` itself or anything sent to the ERP.
const normalizeTitle = title => (title ? title.normalize("NFKC") : title)

/**
 * Presentation only — restyled per the /career redesign. `careerData` is
 * still exactly the array src/pages/career.js fetches from
 * https://staging.visiomate.com/jobpositions and passes in unchanged
 * (same prop, same shape, same fetch, same Apply destination
 * /career/details?id=... — see that file). This component only decides how
 * each role and the loading state look, the same two things it decided
 * before this redesign.
 *
 * Role titles come from `car.display_name` (ERP data) — never hardcoded.
 * Tag pills read `car.department_id` / `car.address_id` /
 * `car.contract_type_id` (department / location / job type) and render only
 * when that field is present with a value that isn't just the company name;
 * a role with none of the three gets no tags, not a placeholder.
 */
const CareerSecond = ({ careerData }) => {
  if (!careerData.length) {
    return (
      <div className="car__roles-loading">
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div className="car__roles-list">
      {careerData.map((car, i) => {
        const tags = [relLabel(car.department_id), relLabel(car.address_id), relLabel(car.contract_type_id)].filter(
          isUsefulTag
        )

        return (
          <div className="car__role-card" key={car.id}>
            <span className="car__role-num" aria-hidden="true">
              {String(i + 1).padStart(2, "0")}
            </span>
            <Link to={`/career/details?id=${car.id}`} className="car__role-main">
              <h3 className="car__role-title">{normalizeTitle(car.display_name)}</h3>
              {tags.length > 0 && (
                <div className="car__role-tags">
                  {tags.map(tag => (
                    <span className="car__role-tag" key={tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              {car.short_description ? (
                <p className="car__role-desc" dangerouslySetInnerHTML={{ __html: car.short_description }} />
              ) : (
                ""
              )}
            </Link>
            <Link to={`/career/details?id=${car.id}`} className="car__role-apply">
              Apply
              <ArrowIcon />
            </Link>
          </div>
        )
      })}
    </div>
  )
}

export default memo(CareerSecond)
